import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Package, ShoppingCart, Box, Factory, Home, Activity, MapPin, Users, BarChart3, Contact } from 'lucide-react';
import {
    Dialog,
    DialogContent,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const SEARCH_LINKS = [
    { path: '/', label: 'Dashboard', icon: Home, section: 'Overview' },
    { path: '/raw-materials', label: 'Raw Materials Master', icon: Package, section: 'Inventory' },
    { path: '/raw-material-inventory', label: 'Raw Material Inventory Levels', icon: Activity, section: 'Inventory' },
    { path: '/bom', label: 'Bill of Materials (BOM)', icon: FileText, section: 'Production' },
    { path: '/finished-goods', label: 'Finished Goods Catalog', icon: Box, section: 'Inventory' },
    { path: '/warehouse-locations', label: 'Warehouse Locations', icon: MapPin, section: 'Inventory' },
    { path: '/purchase-requisitions', label: 'Purchase Requisitions', icon: FileText, section: 'Purchasing' },
    { path: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart, section: 'Purchasing' },
    { path: '/customers', label: 'Customers Directory', icon: Contact, section: 'Sales' },
    { path: '/sales-orders', label: 'Sales Orders', icon: ShoppingCart, section: 'Sales' },
    { path: '/production-orders', label: 'Production Orders', icon: FileText, section: 'Production' },
    { path: '/production', label: 'Active Production Tracking', icon: Factory, section: 'Production' },
    { path: '/completed-production', label: 'Completed Production History', icon: BarChart3, section: 'Production' },
    { path: '/catalog-info', label: 'Edit Catalog Information', icon: FileText, section: 'Marketing' },
    { path: '/brochure-maker', label: 'Brochure Maker', icon: FileText, section: 'Marketing' },
    { path: '/reports', label: 'Reports & Analytics', icon: BarChart3, section: 'Overview' },
    { path: '/users', label: 'User Management', icon: Users, section: 'Admin' },
    { path: '/roles', label: 'Roles & Permissions', icon: Users, section: 'Admin' },
];

const GlobalSearch = ({ open, setOpen, hasViewPermission }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');

    useEffect(() => {
        const down = (e) => {
            if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((open) => !open);
            }
        };
        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, [setOpen]);

    // Filter out links the user doesn't have permission to view
    // and then filter by the search query
    const filteredLinks = SEARCH_LINKS.filter(link => {
        // Map path to module for permission check
        const pathParts = link.path.split('/');
        let module = pathParts[1] || 'dashboard';
        if (module === 'raw-material-inventory') module = 'raw_material_inventory';
        if (module === 'raw-materials') module = 'raw_materials';
        if (module === 'finished-goods') module = 'finished_goods';
        if (module === 'sales-orders') module = 'sales_orders';
        if (module === 'purchase-orders' || module === 'purchase-requisitions') module = 'purchase_orders';
        if (module === 'production-orders' || module === 'completed-production') module = 'production';
        if (module === 'warehouse-locations') module = 'warehouse_locations';
        if (module === 'catalog-info' || module === 'brochure-maker') module = 'catalog';
        if (module === 'roles') module = 'users';

        if (!hasViewPermission(module)) return false;

        if (!query) return true;
        return link.label.toLowerCase().includes(query.toLowerCase()) || link.section.toLowerCase().includes(query.toLowerCase());
    });

    // Group by section
    const groupedLinks = filteredLinks.reduce((acc, link) => {
        if (!acc[link.section]) acc[link.section] = [];
        acc[link.section].push(link);
        return acc;
    }, {});

    const handleSelect = (path) => {
        setOpen(false);
        setQuery('');
        navigate(path);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="p-0 overflow-hidden sm:max-w-[550px] top-[20%] translate-y-0 gap-0 border border-stone-200 shadow-2xl rounded-xl">
                <div className="flex items-center border-b px-4 py-3">
                    <Search className="h-5 w-5 text-stone-400 shrink-0" />
                    <input
                        className="flex-1 bg-transparent border-none outline-none focus:ring-0 px-3 py-1 text-base placeholder:text-stone-400"
                        placeholder="Search pages and modules..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                    />
                    <div className="text-[10px] font-medium text-stone-400 tracking-widest uppercase bg-stone-100 px-1.5 py-0.5 rounded">ESC</div>
                </div>

                <div className="max-h-[400px] overflow-y-auto p-2 scrollbar-hide">
                    {Object.keys(groupedLinks).length === 0 ? (
                        <div className="py-14 text-center text-sm text-stone-500">
                            No results found for "{query}".
                        </div>
                    ) : (
                        Object.entries(groupedLinks).map(([section, links]) => (
                            <div key={section} className="mb-4 last:mb-0">
                                <div className="px-3 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">
                                    {section}
                                </div>
                                <div className="flex flex-col gap-1 mt-1">
                                    {links.map((link) => {
                                        const Icon = link.icon;
                                        return (
                                            <button
                                                key={link.path}
                                                onClick={() => handleSelect(link.path)}
                                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[hsl(145,60%,4%)]/5 text-stone-700 hover:text-[hsl(145,60%,20%)] text-left transition-colors group"
                                            >
                                                <div className="p-1.5 rounded-md bg-stone-100 group-hover:bg-white group-hover:shadow-sm text-stone-500 group-hover:text-[hsl(145,60%,30%)] shrink-0 transition-all">
                                                    <Icon size={16} />
                                                </div>
                                                <span className="text-sm font-medium">{link.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default GlobalSearch;
